import { useCallback, useEffect, useRef, useState } from 'react';
import data from '@emoji-mart/data';
import { init, SearchIndex } from 'emoji-mart';

// Initialize emoji-mart data once
init({ data });

interface EmojiSuggestion {
  id: string;
  native: string;
  name: string;
}

interface UseEmojiShortcodeReturn {
  /** Current suggestions from the :shortcode search */
  suggestions: EmojiSuggestion[];
  /** Whether the suggestion popup should be shown */
  showSuggestions: boolean;
  /** The current search query (text after the colon) */
  query: string;
  /** Call this on every input change to track the colon pattern */
  handleChange: (value: string, cursorPos: number) => void;
  /** Call this when a suggestion is selected — returns the new value with the shortcode replaced */
  selectSuggestion: (emoji: EmojiSuggestion, currentValue: string, cursorPos: number) => { newValue: string; newCursor: number };
  /** Dismiss the suggestions popup */
  dismiss: () => void;
}

/**
 * Hook that detects :shortcode patterns while typing and provides emoji suggestions.
 *
 * Usage:
 *  1. Call handleChange(value, cursorPos) on every onChange
 *  2. Render suggestions when showSuggestions is true
 *  3. Call selectSuggestion when the user picks one
 */
export function useEmojiShortcode(): UseEmojiShortcodeReturn {
  const [suggestions, setSuggestions] = useState<EmojiSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Find the :shortcode pattern before the cursor
  const extractColonQuery = useCallback((value: string, cursorPos: number): string | null => {
    // Look backwards from cursor for a colon
    const textBeforeCursor = value.slice(0, cursorPos);
    const colonMatch = textBeforeCursor.match(/:([a-z0-9_+-]{1,32})$/i);
    if (!colonMatch) return null;

    // Make sure there's no closing colon (user is still typing)
    return colonMatch[1];
  }, []);

  const handleChange = useCallback((value: string, cursorPos: number) => {
    const colonQuery = extractColonQuery(value, cursorPos);

    if (colonQuery && colonQuery.length >= 2) {
      setQuery(colonQuery);

      // Debounce the search
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(async () => {
        try {
          const results = await SearchIndex.search(colonQuery);
          if (results && results.length > 0) {
            const mapped: EmojiSuggestion[] = results.slice(0, 8).map((emoji: { id: string; name: string; skins: { native: string }[] }) => ({
              id: emoji.id,
              native: emoji.skins[0].native,
              name: emoji.name,
            }));
            setSuggestions(mapped);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } catch {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }, 100);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setQuery('');
    }
  }, [extractColonQuery]);

  const selectSuggestion = useCallback((emoji: EmojiSuggestion, currentValue: string, cursorPos: number): { newValue: string; newCursor: number } => {
    // Find the :query before cursor and replace with the native emoji
    const textBeforeCursor = currentValue.slice(0, cursorPos);
    const colonMatch = textBeforeCursor.match(/:([a-z0-9_+-]{1,32})$/i);

    if (colonMatch) {
      const colonStart = cursorPos - colonMatch[0].length;
      const newValue = currentValue.slice(0, colonStart) + emoji.native + currentValue.slice(cursorPos);
      const newCursor = colonStart + emoji.native.length;

      setSuggestions([]);
      setShowSuggestions(false);
      setQuery('');

      return { newValue, newCursor };
    }

    // Fallback: just append
    return { newValue: currentValue + emoji.native, newCursor: currentValue.length + emoji.native.length };
  }, []);

  const dismiss = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setQuery('');
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  return {
    suggestions,
    showSuggestions,
    query,
    handleChange,
    selectSuggestion,
    dismiss,
  };
}
